import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import DataTable from '../../components/AppDataTable'
import { fetchData, formatNumber, getTranslatedValue, toasterrormsg } from '../../../reusable'
import { KTIcon } from '../../../_metronic/helpers'
import { PageTitle } from '../../../_metronic/layout/core'
import { Tr } from '../../../_metronic/i18n/Tr'
import moment from 'moment'
import { useLang } from '../../../_metronic/i18n/Metronici18n'
import { formatDuration } from '../advertisement/bannerAdvertisement/AddAdvertisement.constants'

// register the non-english moment locales used by the language switcher
require('moment/locale/ru')
require('moment/locale/uz-latn')

// app language code -> moment locale (uz translations use the latin script)
const momentLocales: any = { en: 'en', ru: 'ru', uz: 'uz-latn' }

const TAB_KEY = 'impressionReport_activeTab'

const useDebounce = (value: string, delay = 500) => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debounced
}

// ─── Impression Report Tab ────────────────────────────────────────────────────

const ImpressionTab: FC<{ vendorId: string }> = ({ vendorId }) => {
  const t = Tr()
  const lang = useLang()
  const navigate = useNavigate()
  const categoryId = sessionStorage.getItem('categoryId')

  const [watchModalAdsId, setWatchModalAdsId] = useState<string | null>(null)
  const [reviewList, setReviewList] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [focus, setFocus] = useState('')
  const [limit, setLimit] = useState(10)
  const [offset, setOffset] = useState(0)
  const [totalRecords, setTotalRecords] = useState(0)
  const [sortColumn, setSortColumn] = useState('')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  const [month, setMonth] = useState('')
  const [year, setYear] = useState('')

  const debouncedSearch = useDebounce(searchTerm)

  // Month/Year filter is converted to the same startDate/endDate the API expects
  const { startDate, endDate } = useMemo(() => {
    if (!month && !year) return { startDate: '', endDate: '' }
    const y = year || moment().format('YYYY')
    const range = month
      ? moment(`${y}-${month}-01`, 'YYYY-MM-DD')
      : moment(`${y}-01-01`, 'YYYY-MM-DD')
    const unit = month ? 'month' : 'year'
    return {
      startDate: range.startOf(unit).format('YYYY-MM-DD'),
      endDate: range.endOf(unit).format('YYYY-MM-DD'),
    }
  }, [month, year])

  const fetchList = async (term: string) => {
    const param: any = {
      limit,
      offset,
      term: term.replace(/'/g, "''"),
      vendorId,
      startDate: startDate || "",
      endDate: endDate || "",
    }
    const res = await fetchData('public/adsimpressionreport', param)
    if (res.status == '200') {
      setTotalRecords(res.data?.totalRecords ?? res.data?.list?.length ?? 0)
      setReviewList(res.data?.list ?? res.data?.data ?? [])
    } else {
      toasterrormsg(res.message)
    }
  }

  const searchData = (text: any, isfoc: any) => {
    setSearchTerm(text)
    setFocus(isfoc)
    setOffset(0)
  }

  const handleSort = (column: any, dir: 'asc' | 'desc') => {
    setSortColumn(column.sortField)
    setSortDirection(dir)
  }

  const handlePageChange = (newPage: number) => {
    const totalPages = Math.ceil(totalRecords / limit)
    if (newPage === 1) setOffset(0)
    else if (newPage === totalPages) setOffset((totalPages - 1) * limit)
    else setOffset((newPage - 1) * limit)
  }

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setLimit(newRowsPerPage)
    setOffset(0)
  }

  const handleClearFilter = () => {
    setMonth('')
    setYear('')
    setOffset(0)
  }

  useEffect(() => {
    fetchList(debouncedSearch)
  }, [limit, offset, sortColumn, sortDirection, debouncedSearch, categoryId, startDate, endDate])

  const monthOptions = [
    { value: '', label: t('All Months') },
    ...Array.from({ length: 12 }, (_, i) => ({
      value: String(i + 1).padStart(2, '0'),
      label: moment().month(i).locale(momentLocales[lang] || 'en').format('MMMM'),
    })),
  ]

  const currentYear = moment().year()
  const yearOptions = [
    { value: '', label: t('All Years') },
    ...Array.from({ length: 6 }, (_, i) => {
      const y = String(currentYear - i)
      return { value: y, label: y }
    }),
  ]

  const CustomHeader = (props: any) => {
    const inputRef: any = useRef(null)
    const hasFilter = !!(month || year)
    useEffect(() => {
      if (inputRef.current && props.isfoc === 1) inputRef.current.focus()
    }, [props.isfoc])
    return (
      <div className='w-100 my-4'>
        <div className='d-flex align-items-center gap-3 flex-nowrap overflow-auto pb-1'>

          {/* Search */}
          <div className='d-flex align-items-center position-relative my-1 flex-shrink-0'>
            <KTIcon iconName='magnifier' className='fs-1 position-absolute ms-6' />
            <input
              type='text'
              data-kt-user-table-filter='search'
              className='form-control form-control-solid w-250px ps-14'
              placeholder={t('Search Impression')}
              value={props.search}
              onChange={(e: any) => props.fnserch(e.target.value, 1)}
              ref={inputRef}
            />
          </div>

          {/* Divider */}
          <div className='separator separator-vertical h-35px flex-shrink-0' />

          {/* Month / Year */}
          <div className='d-flex align-items-center gap-2 flex-shrink-0'>
            <KTIcon iconName='calendar' className='fs-4 text-muted' />
            <select
              className='form-select form-select-sm form-select-solid'
              style={{ minWidth: '140px' }}
              value={month}
              onChange={(e) => { setMonth(e.target.value); if (e.target.value && !year) setYear(moment().format('YYYY')); setOffset(0) }}
              title={t('Month')}
            >
              {monthOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <select
              className='form-select form-select-sm form-select-solid'
              style={{ minWidth: '110px' }}
              value={year}
              onChange={(e) => { setYear(e.target.value); setOffset(0) }}
              title={t('Year')}
            >
              {yearOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Clear */}
          {hasFilter && (
            <>
              <div className='separator separator-vertical h-35px flex-shrink-0' />
              <button
                className='btn btn-sm btn-icon btn-light-danger flex-shrink-0'
                onClick={handleClearFilter}
                title={t('Clear Filters')}
              >
                <KTIcon iconName='cross' className='fs-4' />
              </button>
            </>
          )}
        </div>

        {/* Active filter chips */}
        {hasFilter && (
          <div className='d-flex align-items-center gap-2 mt-3 flex-wrap'>
            {month && (
              <span className='badge badge-light-info d-flex align-items-center gap-1 py-2 px-3'>
                <span className='fs-8 fw-semibold'>{t('Month')}: {moment(month, 'MM').locale(momentLocales[lang] || 'en').format('MMMM')}</span>
                <button
                  className='btn btn-xs p-0 ms-1 text-info'
                  style={{ lineHeight: 1 }}
                  onClick={() => { setMonth(''); setOffset(0) }}
                >
                  <KTIcon iconName='cross' className='fs-8' />
                </button>
              </span>
            )}
            {year && (
              <span className='badge badge-light-info d-flex align-items-center gap-1 py-2 px-3'>
                <span className='fs-8 fw-semibold'>{t('Year')}: {year}</span>
                <button
                  className='btn btn-xs p-0 ms-1 text-info'
                  style={{ lineHeight: 1 }}
                  onClick={() => { setYear(''); setOffset(0) }}
                >
                  <KTIcon iconName='cross' className='fs-8' />
                </button>
              </span>
            )}
          </div>
        )}
      </div>
    )
  }

  // API doesn't return perDayPrice for banner ads, so derive it from price and the ad's date range
  const getPerDayPrice = (row: any) => {
    if (row.perDayPrice != null) return row.perDayPrice
    if (row.price == null || !row.startDate || !row.endDate) return null
    const days = Math.max(1, Math.round(moment(row.endDate).diff(moment(row.startDate), 'days', true)))
    return Math.round(row.price / days)
  }

  const column = useMemo(
    () => [
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('#')}</label>,
        selector: (_row: any, index: any) => index + 1,
        width: '60px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Banner Title')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>
              {getTranslatedValue(lang, row, '_title') || row.title || row.name || '-'}
            </div>
          </div>
        ),
        sortable: true,
        sortField: 'title',
        width: '200px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Status')}</label>,
        cell: (row: any) => {
          const status = row.status
          return (
            <div className='my-3'>
              {status === 'DRAFT' && <span className='badge badge-secondary mx-2'>{t('DRAFT')}</span>}
              {status === 'UNDER_REVIEW' && <span className='badge badge-warning mx-2'>{t('UNDER_REVIEW')}</span>}
              {status === 'APPROVED' && <span className='badge badge-success mx-2'>{t('APPROVED')}</span>}
              {status === 'ACTIVATED' && <span className='badge badge-primary mx-2'>{t('ACTIVATED')}</span>}
              {status === 'REJECTED' && <span className='badge badge-danger mx-2'>{t('REJECTED')}</span>}
              {status === 'PAUSED' && <span className='badge badge-info mx-2'>{t('PAUSED')}</span>}
              {status === 'COMPLETED' && <span className='badge badge-success mx-2'>{t('COMPLETED')}</span>}
              {!status && <span className='badge badge-secondary mx-2'>-</span>}
            </div>
          )
        },
        sortable: true,
        width: '180px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Placement')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>
              {(lang === 'en' ? row.placementName : row[`${lang}_placementName`]) || row.placementName || '-'}
            </div>
          </div>
        ),
        sortable: false,
        width: '180px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted text-nowrap'>{t('View Count')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{row.viewCount != null ? formatNumber(Number(row.viewCount)) : '-'}</div>
          </div>
        ),
        sortable: true,
        width: '120px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Duration')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{formatDuration(row?.duration, row?.durationType, lang)}</div>
          </div>
        ),
        sortable: false,
        width: '150px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Per day Price')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{getPerDayPrice(row) != null ? `${formatNumber(getPerDayPrice(row))} UZS` : '-'}</div>
          </div>
        ),
        sortable: false,
        width: '140px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Total Price')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{row.price != null ? `${formatNumber(row.price)} UZS` : '-'}</div>
          </div>
        ),
        sortable: false,
        width: '130px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Start Date')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>
              {row.startDate ? moment(row.startDate).format('DD.MM.YYYY') : '-'}
            </div>
          </div>
        ),
        sortable: false,
        width: '150px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('End Date')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>
              {row.endDate ? moment(row.endDate).format('DD.MM.YYYY') : '-'}
            </div>
          </div>
        ),
        sortable: false,
        width: '150px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('View Users')}</label>,
        cell: (row: any) => (
          <div className='my-2 mw-100'>
            <button
              className='btn btn-sm btn-primary d-flex align-items-center gap-2 mw-100 text-start'
              onClick={() => setWatchModalAdsId(String(row.adsId || row.id || row._id || ''))}
              title={t('View watched users')}
            >
              <KTIcon iconName='eye' className='fs-5 text-white flex-shrink-0' />
              <span className='fs-7 fw-semibold text-white text-break'>{t('View Users')}</span>
            </button>
          </div>
        ),
        width: '200px',
        ignoreRowClick: true,
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('View Ad Details')}</label>,
        cell: (row: any) => (
          <div className='my-2'>
            <button
              className='btn btn-sm btn-icon btn-color-info btn-active-light-info'
              title={t('View')}
              onClick={() => navigate('/advertisementdetail', { state: row })}
            >
              <KTIcon iconName='eye' className='fs-2x text-info' />
            </button>
          </div>
        ),
        width: '150px',
        ignoreRowClick: true,
      },
    ],
    [categoryId, lang]
  )

  return (
    <>
      <DataTable
        columns={column}
        data={reviewList}
        pagination
        paginationServer
        onChangePage={handlePageChange}
        paginationTotalRows={totalRecords}
        paginationPerPage={limit}
        onChangeRowsPerPage={handleRowsPerPageChange}
        paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 30]}
        subHeader
        subHeaderComponent={<CustomHeader search={searchTerm} isfoc={focus} fnserch={searchData} />}
        onSort={handleSort}
        paginationComponentOptions={{ rowsPerPageText: t('Rows Per Page') }}
        sortServer
      />
      {watchModalAdsId && (
        <WatchedUsersModal
          source='ads'
          vendorId={vendorId}
          adsId={watchModalAdsId}
          onClose={() => setWatchModalAdsId(null)}
        />
      )}
    </>
  )
}

// ─── Watched Users Modal ──────────────────────────────────────────────────────

// Banner Ads → `public/adsviewslist` (vendorId + adsId).
// Search Ads → `public/sponser/viewslist` (sponserId).
type WatchedUsersModalProps =
  | { source: 'ads'; vendorId: string; adsId: string; sponserId?: never; onClose: () => void }
  | { source: 'sponser'; sponserId: string; vendorId?: never; adsId?: never; onClose: () => void }

const WatchedUsersModal: FC<WatchedUsersModalProps> = ({
  source,
  vendorId,
  adsId,
  sponserId,
  onClose,
}) => {
  const t = Tr()
  const [userList, setUserList] = useState<any[]>([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [imageBaseUrl, setImageBaseUrl] = useState('')
  const [limit, setLimit] = useState(10)
  const [offset, setOffset] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(false)
  const searchRef: any = useRef(null)

  const debouncedSearch = useDebounce(searchTerm)

  const fetchWatchedUsers = async (term: string) => {
    setLoading(true)
    const search = term.replace(/'/g, "''")
    const url = source === 'ads' ? 'public/adsviewslist' : 'public/sponser/viewslist'
    const param: any =
      source === 'ads'
        ? { vendorId, adsId, limit, offset, term: search }
        : { sponserId, limit, offset, search }
    const res = await fetchData(url, param)
    setLoading(false)
    if (res.status == 200 || res.status == '200') {
      setUserList(res.data?.list ?? res.data?.data ?? [])
      setTotalRecords(res.data?.totalRecords ?? res.data?.totalRecord ?? res.data?.list?.length ?? 0)
      setImageBaseUrl(res?.url ?? '')
    } else {
      toasterrormsg(res.message)
    }
  }

  useEffect(() => {
    fetchWatchedUsers(debouncedSearch)
  }, [limit, offset, debouncedSearch])

  const handlePageChange = (newPage: number) => {
    const totalPages = Math.ceil(totalRecords / limit)
    if (newPage === 1) setOffset(0)
    else if (newPage === totalPages) setOffset((totalPages - 1) * limit)
    else setOffset((newPage - 1) * limit)
  }

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setLimit(newRowsPerPage)
    setOffset(0)
  }

  const handleSearch = (val: string) => {
    setSearchTerm(val)
    setOffset(0)
  }

  const columns = useMemo(
    () => [
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('#')}</label>,
        cell: (_row: any, index: any) => (
          <div className='fw-semibold fs-6'>{offset + index + 1}</div>
        ),
        width: '60px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('User Name')}</label>,
        cell: (row: any) => (
          <div className='d-flex align-items-center gap-3 my-2'>
            {row.profile ? (
              <img
                src={imageBaseUrl + row.profile}
                alt=''
                className='rounded-circle'
                style={{ width: 36, height: 36, objectFit: 'cover' }}
              />
            ) : (
              <div
                className='rounded-circle bg-light-primary d-flex align-items-center justify-content-center'
                style={{ width: 36, height: 36, minWidth: 36 }}
              >
                <KTIcon iconName='user' className='fs-4 text-primary' />
              </div>
            )}
            <div className='fw-bold fs-6'>{row.fullName || '-'}</div>
          </div>
        ),
        grow: 2,
        minWidth: '180px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('View Count')}</label>,
        cell: (row: any) => (
          <div className='fw-bold fs-6 text-center w-100'>{row.viewCount ?? '-'}</div>
        ),
        width: '120px',
      },
    ],
    [offset, imageBaseUrl]
  )

  return (
    <div
      className='modal fade show d-block'
      tabIndex={-1}
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
      onClick={(e) => { if ((e.target as HTMLElement).classList.contains('modal')) onClose() }}
    >
      <div className='modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable'>
        <div className='modal-content'>
          {/* Header */}
          <div className='modal-header py-4'>
            <div className='d-flex align-items-center gap-3'>
              <div className='bg-light-primary rounded p-2'>
                <KTIcon iconName='eye' className='fs-3 text-primary' />
              </div>
              <div>
                <h4 className='modal-title fw-bold mb-0'>{t('Watched Users')}</h4>
                <span className='text-muted fs-7'>{totalRecords} {t('total users')}</span>
              </div>
            </div>
            <button type='button' className='btn-close' onClick={onClose} />
          </div>

          {/* Search */}
          <div className='modal-body pb-0 pt-4'>
            <div className='d-flex align-items-center position-relative mb-4'>
              <KTIcon iconName='magnifier' className='fs-1 position-absolute ms-4' />
              <input
                ref={searchRef}
                type='text'
                className='form-control form-control-solid ps-13'
                placeholder={t('Search users...')}
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                autoFocus
              />
            </div>

            {loading ? (
              <div className='d-flex justify-content-center align-items-center py-10'>
                <span className='spinner-border spinner-border-sm text-primary me-3' />
                <span className='text-muted fs-6'>{t('Loading...')}</span>
              </div>
            ) : userList.length === 0 ? (
              <div className='d-flex flex-column align-items-center py-10 text-muted'>
                <KTIcon iconName='information' className='fs-2x mb-3' />
                <span className='fs-6'>{t('No watched users found')}</span>
              </div>
            ) : (
              <DataTable
                columns={columns}
                data={userList}
                pagination
                paginationServer
                onChangePage={handlePageChange}
                paginationTotalRows={totalRecords}
                paginationPerPage={limit}
                onChangeRowsPerPage={handleRowsPerPageChange}
                paginationRowsPerPageOptions={[5, 10, 20]}
                paginationComponentOptions={{ rowsPerPageText: t('Rows Per Page') }}
                noTableHead={false}
              />
            )}
          </div>

          <div className='modal-footer py-3'>
            <button type='button' className='btn btn-light btn-sm' onClick={onClose}>
              {t('Close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Sponsored Ads Tab ────────────────────────────────────────────────────────

const SponsoredTab: FC<{ vendorId: string }> = ({ vendorId }) => {
  const t = Tr()
  const lang = useLang()
  const navigate = useNavigate()

  const [list, setList] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [focus, setFocus] = useState('')
  const [limit, setLimit] = useState(10)
  const [offset, setOffset] = useState(0)
  const [totalRecords, setTotalRecords] = useState(0)
  const [month, setMonth] = useState('')
  const [year, setYear] = useState('')
  const [status, setStatus] = useState('')
  const [watchModalSponserId, setWatchModalSponserId] = useState<string | null>(null)

  const debouncedSearch = useDebounce(searchTerm)

  // Month/Year filter is converted to the same startDate/endDate the API expects
  const { startDate, endDate } = useMemo(() => {
    if (!month && !year) return { startDate: '', endDate: '' }
    const y = year || moment().format('YYYY')
    const range = month
      ? moment(`${y}-${month}-01`, 'YYYY-MM-DD')
      : moment(`${y}-01-01`, 'YYYY-MM-DD')
    const unit = month ? 'month' : 'year'
    return {
      startDate: range.startOf(unit).format('YYYY-MM-DD'),
      endDate: range.endOf(unit).format('YYYY-MM-DD'),
    }
  }, [month, year])

  const fetchList = async (term: string) => {
    const param: any = {
      vendorId,
      limit,
      offset,
      term: term.replace(/'/g, "''"),
      sortBy: 'created',
      sort: 'desc',
      startDate: startDate || '',
      endDate: endDate || '',
    }
    if (status) param.status = status

    const res = await fetchData('public/sponser/impressionreport', param)
    if (res.status == '200') {
      setList(res.data?.list ?? [])
      setTotalRecords(Number(res.data?.totalRecord ?? res.data?.totalRecords ?? res.data?.list?.length ?? 0))
    } else {
      toasterrormsg(res.message)
    }
  }

  const searchData = (text: any, isfoc: any) => {
    setSearchTerm(text)
    setFocus(isfoc)
    setOffset(0)
  }

  const handlePageChange = (newPage: number) => {
    const totalPages = Math.ceil(totalRecords / limit)
    if (newPage === 1) setOffset(0)
    else if (newPage === totalPages) setOffset((totalPages - 1) * limit)
    else setOffset((newPage - 1) * limit)
  }

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setLimit(newRowsPerPage)
    setOffset(0)
  }

  // const handleDateFilter = () => {
  //   setOffset(0)
  //   fetchList()
  // }

  const handleClearFilter = () => {
    setMonth('')
    setYear('')
    setStatus('')
    setOffset(0)
  }

  useEffect(() => {
    fetchList(debouncedSearch)
  }, [limit, offset, debouncedSearch, status, startDate, endDate])

  const statusOptions = [
    { value: '', label: t('All Status') },
    { value: 'DRAFT', label: t('Draft') },
    { value: 'UNDER_REVIEW', label: t('Under Review') },
    { value: 'APPROVED', label: t('Approved') },
    { value: 'REJECTED', label: t('Rejected') },
    { value: 'PAUSED', label: t('Paused') },
    { value: 'COMPLETED', label: t('Completed') },
  ]

  const monthOptions = [
    { value: '', label: t('All Months') },
    ...Array.from({ length: 12 }, (_, i) => ({
      value: String(i + 1).padStart(2, '0'),
      label: moment().month(i).locale(momentLocales[lang] || 'en').format('MMMM'),
    })),
  ]

  const currentYear = moment().year()
  const yearOptions = [
    { value: '', label: t('All Years') },
    ...Array.from({ length: 6 }, (_, i) => {
      const y = String(currentYear - i)
      return { value: y, label: y }
    }),
  ]

  const CustomHeader = (props: any) => {
    const inputRef: any = useRef(null)
    const hasFilter = !!(month || year || status)
    useEffect(() => {
      if (inputRef.current && props.isfoc === 1) inputRef.current.focus()
    }, [props.isfoc])
    return (
      <div className='w-100 my-4'>
        <div className='d-flex align-items-center gap-3 flex-nowrap overflow-auto pb-1'>

          {/* Search */}
          <div className='d-flex align-items-center position-relative my-1 flex-shrink-0'>
            <KTIcon iconName='magnifier' className='fs-1 position-absolute ms-6' />
            <input
              type='text'
              data-kt-user-table-filter='search'
              className='form-control form-control-solid w-250px ps-14'
              placeholder={t('Search Ads')}
              value={props.search}
              onChange={(e: any) => props.fnserch(e.target.value, 1)}
              ref={inputRef}
            />
          </div>

          {/* Divider */}
          <div className='separator separator-vertical h-35px flex-shrink-0' />

          {/* Status */}
          <div className='d-flex align-items-center gap-2 flex-shrink-0'>
            <KTIcon iconName='filter' className='fs-4 text-muted' />
            <select
              className='form-select form-select-sm form-select-solid'
              style={{ minWidth: '145px' }}
              value={status}
              onChange={(e) => { setStatus(e.target.value); setOffset(0) }}
            >
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Divider */}
          <div className='separator separator-vertical h-35px flex-shrink-0' />

          {/* Month / Year */}
          <div className='d-flex align-items-center gap-2 flex-shrink-0'>
            <KTIcon iconName='calendar' className='fs-4 text-muted' />
            <select
              className='form-select form-select-sm form-select-solid'
              style={{ minWidth: '140px' }}
              value={month}
              onChange={(e) => { setMonth(e.target.value); if (e.target.value && !year) setYear(moment().format('YYYY')); setOffset(0) }}
              title={t('Month')}
            >
              {monthOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <select
              className='form-select form-select-sm form-select-solid'
              style={{ minWidth: '110px' }}
              value={year}
              onChange={(e) => { setYear(e.target.value); setOffset(0) }}
              title={t('Year')}
            >
              {yearOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Clear */}
          {hasFilter && (
            <>
              <div className='separator separator-vertical h-35px flex-shrink-0' />
              <button
                className='btn btn-sm btn-icon btn-light-danger flex-shrink-0'
                onClick={handleClearFilter}
                title={t('Clear Filters')}
              >
                <KTIcon iconName='cross' className='fs-4' />
              </button>
            </>
          )}
        </div>

        {/* Active filter chips */}
        {hasFilter && (
          <div className='d-flex align-items-center gap-2 mt-3 flex-wrap'>
            {status && (
              <span className='badge badge-light-primary d-flex align-items-center gap-1 py-2 px-3'>
                <span className='fs-8 fw-semibold'>{t('Status')}: {status.replace('_', ' ')}</span>
                <button
                  className='btn btn-xs p-0 ms-1 text-primary'
                  style={{ lineHeight: 1 }}
                  onClick={() => { setStatus(''); setOffset(0) }}
                >
                  <KTIcon iconName='cross' className='fs-8' />
                </button>
              </span>
            )}
            {month && (
              <span className='badge badge-light-info d-flex align-items-center gap-1 py-2 px-3'>
                <span className='fs-8 fw-semibold'>{t('Month')}: {moment(month, 'MM').locale(momentLocales[lang] || 'en').format('MMMM')}</span>
                <button
                  className='btn btn-xs p-0 ms-1 text-info'
                  style={{ lineHeight: 1 }}
                  onClick={() => { setMonth(''); setOffset(0) }}
                >
                  <KTIcon iconName='cross' className='fs-8' />
                </button>
              </span>
            )}
            {year && (
              <span className='badge badge-light-info d-flex align-items-center gap-1 py-2 px-3'>
                <span className='fs-8 fw-semibold'>{t('Year')}: {year}</span>
                <button
                  className='btn btn-xs p-0 ms-1 text-info'
                  style={{ lineHeight: 1 }}
                  onClick={() => { setYear(''); setOffset(0) }}
                >
                  <KTIcon iconName='cross' className='fs-8' />
                </button>
              </span>
            )}
          </div>
        )}
      </div>
    )
  }

  const column = useMemo(
    () => [
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('#')}</label>,
        selector: (_row: any, index: any) => index + 1,
        width: '60px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Property Name')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>
              {getTranslatedValue(lang, row, '_name') || row.en_name || row.ru_name || row.uz_name || '-'}
            </div>
          </div>
        ),
        width: '180px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Status')}</label>,
        cell: (row: any) => {
          const s = row.status
          return (
            <div className='my-3'>
              {s === 'DRAFT' && <span className='badge badge-secondary'>{t('DRAFT')}</span>}
              {s === 'UNDER_REVIEW' && <span className='badge badge-warning'>{t('UNDER REVIEW')}</span>}
              {s === 'APPROVED' && <span className='badge badge-success'>{t('APPROVED')}</span>}
              {s === 'ACTIVATED' && <span className='badge badge-primary'>{t('ACTIVATED')}</span>}
              {s === 'REJECTED' && <span className='badge badge-danger'>{t('REJECTED')}</span>}
              {s === 'PAUSED' && <span className='badge badge-info'>{t('PAUSED')}</span>}
              {s === 'COMPLETED' && <span className='badge badge-success'>{t('COMPLETED')}</span>}
              {!s && <span className='badge badge-secondary'>-</span>}
            </div>
          )
        },
        width: '150px',
      },
      // {
      //   name: <label className='fw-bold fs-6 text-muted'>{t('Vendor Name')}</label>,
      //   cell: (row: any) => (
      //     <div className='my-3'>
      //       <div className='fw-bold fs-5'>{row.fullName || '-'}</div>
      //     </div>
      //   ),
      //   width: '160px',
      // },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Property Type')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5 text-capitalize'>
              {getTranslatedValue(lang, row, '_categoryName') || row.en_categoryName || row.actionType || '-'}
            </div>
          </div>
        ),
        width: '130px',
      },
       {
        name: <label className='fw-bold fs-6 text-muted'>{t('View Count')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{row.totalImpressions != null ? formatNumber(Number(row.totalImpressions)) : '-'}</div>
          </div>
        ),
        width: '120px',
      },
       {
        name: <label className='fw-bold fs-6 text-muted'>{t('Click')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{row.totalViews != null ? formatNumber(Number(row.totalViews)) : '-'}</div>
          </div>
        ),
        width: '100px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('CTR (%)')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{row.ctr != null ? `${row.ctr}%` : '-'}</div>
          </div>
        ),
        width: '100px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Duration')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>
              {formatDuration(row?.duration, row?.durationType, lang)}
            </div>
          </div>
        ),
        width: '120px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Per Day Price')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{row.perDayPrice != null ? formatNumber(row.perDayPrice) + ' UZS' : '-'}</div>
          </div>
        ),
        width: '130px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Total Price')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{row.price != null ? `${formatNumber(row.price)} UZS` : '-'}</div>
          </div>
        ),
        width: '120px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Start Date')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>
              {row.startDate ? moment(row.startDate).format('DD.MM.YYYY') : '-'}
            </div>
          </div>
        ),
        width: '130px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('End Date')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>
              {row.endDate ? moment(row.endDate).format('DD.MM.YYYY') : '-'}
            </div>
          </div>
        ),
        width: '130px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('View Users')}</label>,
        cell: (row: any) => (
          <div className='my-2 mw-100'>
            <button
              className='btn btn-sm btn-primary d-flex align-items-center gap-2 mw-100 text-start'
              onClick={() => setWatchModalSponserId(String(row.sponserId || row.id || row._id || ''))}
              title={t('View watched users')}
            >
              <KTIcon iconName='eye' className='fs-5 text-white flex-shrink-0' />
              <span className='fs-7 fw-semibold text-white text-break'>{t('View Users')}</span>
            </button>
          </div>
        ),
        width: '200px',
        ignoreRowClick: true,
      },
            {
        name: <label className='fw-bold fs-6 text-muted'>{t('View Detail')}</label>,
        cell: (row: any) => (
          <div className='my-2'>
            <button
              className='btn btn-sm btn-icon btn-color-info btn-active-light-info'
              title={t('View')}
              onClick={() =>
                navigate('/viewsponsuredadvertismentdetail', {
                  state: { ...row, sponserId: row.sponserId || row.id || row._id },
                })
              }
            >
              <KTIcon iconName='eye' className='fs-2x text-info' />
            </button>
          </div>
        ),
        width: '130px',
        ignoreRowClick: true,
      },
      // {
      //   name: <label className='fw-bold fs-6 text-muted'>{t('Created')}</label>,
      //   cell: (row: any) => (
      //     <div className='my-3'>
      //       <div className='fw-bold fs-5'>
      //         {row.created ? moment(row.created).format('DD.MM.YYYY') : '-'}
      //       </div>
      //     </div>
      //   ),
      //   width: '130px',
      // },
    ],
    [lang]
  )

  return (
    <>
      <DataTable
        columns={column}
        data={list}
        pagination
        paginationServer
        onChangePage={handlePageChange}
        paginationTotalRows={totalRecords}
        paginationPerPage={limit}
        onChangeRowsPerPage={handleRowsPerPageChange}
        paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 30]}
        subHeader
        subHeaderComponent={<CustomHeader search={searchTerm} isfoc={focus} fnserch={searchData} />}
        paginationComponentOptions={{ rowsPerPageText: t('Rows Per Page') }}
      />
      {watchModalSponserId && (
        <WatchedUsersModal
          source='sponser'
          sponserId={watchModalSponserId}
          onClose={() => setWatchModalSponserId(null)}
        />
      )}
    </>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

const ImpressionReport: FC = () => {
  const t = Tr()

  var vendorId = ''
  const vendorData = sessionStorage.getItem('vendor')
  if (vendorData) {
    var vendor = JSON.parse(vendorData)
    vendorId = vendor.vendorId
  }

  const [activeTab, setActiveTab] = useState<'impression' | 'sponsored'>(() => {
    const saved = localStorage.getItem(TAB_KEY)
    return saved === 'sponsored' ? 'sponsored' : 'impression'
  })

  const handleTabChange = (tab: 'impression' | 'sponsored') => {
    setActiveTab(tab)
    localStorage.setItem(TAB_KEY, tab)
  }

  return (
    <>
      <ToastContainer />
      <PageTitle breadcrumbs={[]}>{t('View Impression Report')}</PageTitle>

      <div className='card card-flush py-4'>
        <div className='card-header pt-0 border-0'>
          <ul className='nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bold'>
            <li className='nav-item'>
              <button
                className={`nav-link text-active-primary pb-2 ${activeTab === 'impression' ? 'active' : ''}`}
                onClick={() => handleTabChange('impression')}
              >
                {t('Banner Ads')}
              </button>
            </li>
            <li className='nav-item'>
              <button
                className={`nav-link text-active-primary pb-2 ${activeTab === 'sponsored' ? 'active' : ''}`}
                onClick={() => handleTabChange('sponsored')}
              >
                {t('Search Ads')}
              </button>
            </li>
          </ul>
        </div>

        <div className='card-body pt-0'>
          {activeTab === 'impression' && <ImpressionTab vendorId={vendorId} />}
          {activeTab === 'sponsored' && <SponsoredTab vendorId={vendorId} />}
        </div>
      </div>
    </>
  )
}

export default ImpressionReport
